import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { Milestone } from './types';

interface DuplicateActionsDialogProps {
  open: boolean;
  milestones: Milestone[];
  selectedCount: number;
  duplicateInProgress: boolean;
  onClose: () => void;
  onConfirm: (targetMilestoneName: string, targetStepName: string) => void;
}

export const DuplicateActionsDialog: React.FC<DuplicateActionsDialogProps> = ({
  open,
  milestones,
  selectedCount,
  duplicateInProgress,
  onClose,
  onConfirm,
}) => {
  const milestoneNames = useMemo(
    () => milestones.slice().map(({ name }) => name),
    [milestones]
  );

  const [targetMilestoneName, setTargetMilestoneName] = useState<string>('');
  const [targetStepName, setTargetStepName] = useState<string>('');

  const targetMilestone = useMemo(
    () => milestones.find(({ name }) => name === targetMilestoneName) || null,
    [milestones, targetMilestoneName]
  );

  const stepNames = useMemo(() => {
    if (!targetMilestone) {
      return [];
    }

    return targetMilestone.steps
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(({ name }) => name);
  }, [targetMilestone]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const [defaultMilestoneName = ''] = milestoneNames;

    const defaultMilestone = defaultMilestoneName 
      ? milestones.find(({ name }) => name === defaultMilestoneName)
      : null;

    const defaultStepName = (defaultMilestone?.steps || [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0]?.name || '';

    setTargetMilestoneName(defaultMilestoneName);
    setTargetStepName(defaultStepName);
  }, [open, milestoneNames, milestones]);

  useEffect(() => {
    if (!targetMilestone) {
      setTargetStepName('');
      return;
    }

    if (targetStepName && stepNames.includes(targetStepName)) {
      return;
    }
    
    setTargetStepName(stepNames[0] || '');
  }, [targetMilestone, stepNames, targetStepName]);

  const canConfirm = Boolean(targetMilestoneName && targetStepName && selectedCount > 0);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={duplicateInProgress ? undefined : onClose}
    >
      <DialogTitle>Duplicate Actions</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Selected actions:
          {' '}
          {selectedCount}
        </Typography>

        <FormControl
          fullWidth
          margin="normal"
        >
          <InputLabel>Target Milestone</InputLabel>
          <Select
            value={targetMilestoneName}
            onChange={(e) => setTargetMilestoneName(e.target.value as string)}
          >
            {milestoneNames.map((name) => (
              <MenuItem
                key={name}
                value={name}
              >
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          disabled={!targetMilestoneName}
          margin="normal"
        >
          <InputLabel>Target Step</InputLabel>
          <Select
            value={targetStepName}
            onChange={(e) => setTargetStepName(e.target.value as string)}
          >
            {stepNames.map((name) => (
              <MenuItem
                key={name}
                value={name}
              >
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={duplicateInProgress}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={!canConfirm || duplicateInProgress}
          variant="contained"
          onClick={() => onConfirm(targetMilestoneName, targetStepName)}
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
};
